import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import { usePendingCount } from '../lib/approvals';
import { useUnreadCount } from '../lib/notifications';
import { useNavCounts, type NavCounts } from '../lib/nav-counts';
import { Icon, type IconName, initials } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  adminOnly?: boolean;
  staffOnly?: boolean;
}

interface NavGroup {
  group: string;
  key: string;
  adminOnly?: boolean;
  staffOnly?: boolean;
  items: NavItem[];
}

type NavEntry =
  | { type: 'item'; item: NavItem }
  | { type: 'group'; group: NavGroup };

/** Which directory each nav item's size badge counts. */
const NAV_COUNT_KEY: Partial<Record<string, keyof NavCounts>> = {
  '/clients': 'clients',
  '/suppliers': 'suppliers',
  '/subcontractors': 'subcontractors',
  '/materials': 'materials',
  '/bank-accounts': 'bankAccounts',
  '/team': 'staff',
};

/**
 * Navigation order requested:
 * 1. Dashboard
 * 2. Masters (Client, Supplier, Sub contractor, Material)
 * 3. Transaction (Client, Supplier, Sub contractor, Other Expenses)
 * 4. Project Drawings
 * 5. Reports
 * 6. Finance & Stock (Bank Accounts, Stock Transfer, Fund Transfer)
 * 7. Site Operations (Approvals, Site team)
 * 8. Settings
 */
const NAV_ENTRIES: NavEntry[] = [
  {
    type: 'item',
    item: { to: '/', label: 'Dashboard', icon: 'grid' },
  },
  {
    type: 'item',
    item: { to: '/my-entries', label: 'My Entries', icon: 'clipboard', staffOnly: true },
  },
  {
    type: 'group',
    group: {
      group: 'Masters',
      key: 'masters',
      adminOnly: true,
      items: [
        { to: '/clients', label: 'Client', icon: 'users', adminOnly: true },
        { to: '/suppliers', label: 'Supplier', icon: 'truck', adminOnly: true },
        { to: '/subcontractors', label: 'Sub contractor', icon: 'hardhat', adminOnly: true },
        { to: '/materials', label: 'Material', icon: 'box', adminOnly: true },
      ],
    },
  },
  {
    type: 'group',
    group: {
      group: 'Transaction',
      key: 'transaction',
      adminOnly: true,
      items: [
        { to: '/transactions/clients', label: 'Client', icon: 'users', adminOnly: true },
        { to: '/transactions/suppliers', label: 'Supplier', icon: 'truck', adminOnly: true },
        { to: '/transactions/subcontractors', label: 'Sub contractor', icon: 'hardhat', adminOnly: true },
        { to: '/transactions/expenses', label: 'Other Expenses', icon: 'receipt', adminOnly: true },
      ],
    },
  },
  {
    type: 'item',
    item: { to: '/project-drawings', label: 'Project Drawings', icon: 'layers' },
  },
  {
    type: 'item',
    item: { to: '/reports', label: 'Reports', icon: 'filetext', adminOnly: true },
  },
  {
    type: 'group',
    group: {
      group: 'Finance & Stock',
      key: 'finance_stock',
      items: [
        { to: '/bank-accounts', label: 'Bank Accounts', icon: 'bank', adminOnly: true },
        { to: '/stock-transfers', label: 'Stock Transfer', icon: 'repeat' },
        { to: '/fund-transfers', label: 'Fund Transfer', icon: 'transfer', adminOnly: true },
      ],
    },
  },
  {
    type: 'group',
    group: {
      group: 'Site Operations',
      key: 'site_operations',
      adminOnly: true,
      items: [
        { to: '/approvals', label: 'Approvals', icon: 'inbox', adminOnly: true },
        { to: '/team', label: 'Site team', icon: 'shield', adminOnly: true },
      ],
    },
  },
  {
    type: 'item',
    item: { to: '/settings', label: 'Settings', icon: 'sliders', adminOnly: true },
  },
];

const STORAGE_KEY = 'vns_sidebar_open_groups';

function isItemActive(to: string, pathname: string): boolean {
  if (to === '/') {
    return pathname === '/' || pathname === '';
  }
  if (to === '/transactions/clients') {
    return pathname === '/transactions/clients' || pathname === '/transactions';
  }
  if (to === '/clients') {
    return pathname === '/clients' || pathname.startsWith('/clients/') || pathname.startsWith('/projects/');
  }
  if (pathname === to) {
    return true;
  }
  if (pathname.startsWith(to + '/')) {
    return true;
  }
  return false;
}

function getActiveGroupKey(pathname: string): string | null {
  for (const entry of NAV_ENTRIES) {
    if (entry.type === 'group') {
      const containsActive = entry.group.items.some((i) =>
        isItemActive(i.to, pathname),
      );
      if (containsActive) {
        return entry.group.key;
      }
    }
  }
  return null;
}

export default function Shell({
  title, crumb, actions, children,
}: {
  title: string; crumb?: string; actions?: ReactNode; children: ReactNode;
}) {
  const { actor, signOut } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Accordion behavior: only one dropdown open at a time.
  // Initially, only open if the current page belongs inside a group;
  // if on Dashboard or other top-level page, all dropdowns start closed.
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(() =>
    getActiveGroupKey(location.pathname),
  );

  const { data: pending } = usePendingCount();
  const { data: counts } = useNavCounts();
  const unread = useUnreadCount();

  // Clear legacy stored open groups so old multi-open sessions don't linger
  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  // When route changes, open the active group (if any) and close all others
  useEffect(() => {
    setOpenGroupKey(getActiveGroupKey(location.pathname));
  }, [location.pathname]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  if (!actor) return null;
  const admin = isAdmin(actor);

  function toggleGroup(key: string) {
    // Accordion: clicking an open group closes it; clicking any other group opens only that group
    setOpenGroupKey((prev) => (prev === key ? null : key));
  }

  const visibleEntries = NAV_ENTRIES.filter((entry) => {
    if (entry.type === 'item') {
      return admin ? !entry.item.staffOnly : !entry.item.adminOnly;
    }
    if (admin) return !entry.group.staffOnly;
    return !entry.group.adminOnly;
  });

  return (
    <div className="app">
      <aside className={menuOpen ? 'sidebar open' : 'sidebar'}>
        <div className="brand">
          <div className="brand-mark">VN</div>
          <div>
            <div className="brand-name">VNS ERP</div>
            <div className="brand-tag">Construction Suite</div>
          </div>
        </div>

        <nav className="nav">
          {visibleEntries.map((entry) => {
            if (entry.type === 'item') {
              const item = entry.item;
              const countKey = NAV_COUNT_KEY[item.to];
              const active = isItemActive(item.to, location.pathname);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => (isActive || active ? 'nav-item active' : 'nav-item')}
                >
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                  {item.to === '/approvals' && !!pending && (
                    <span className="count alert">{pending}</span>
                  )}
                  {countKey && counts && (
                    <span className="count">{counts[countKey]}</span>
                  )}
                </NavLink>
              );
            }

            const g = entry.group;
            const items = g.items.filter((i) => (admin ? !i.staffOnly : !i.adminOnly));
            if (items.length === 0) return null;

            const hasActiveChild = items.some((i) => isItemActive(i.to, location.pathname));
            const isOpen = openGroupKey === g.key;

            return (
              <div key={g.key} style={{ marginBottom: 4 }}>
                <button
                  type="button"
                  className={`nav-group-btn ${hasActiveChild ? 'has-active' : ''}`}
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={isOpen}
                >
                  <span>{g.group}</span>
                  <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} />
                </button>

                {isOpen && (
                  <div style={{ paddingLeft: 6 }}>
                    {items.map((item) => {
                      const countKey = NAV_COUNT_KEY[item.to];
                      const active = isItemActive(item.to, location.pathname);
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.to === '/'}
                          onClick={() => setMenuOpen(false)}
                          className={({ isActive }) =>
                            isActive || active ? 'nav-item active' : 'nav-item'
                          }
                        >
                          <Icon name={item.icon} />
                          <span>{item.label}</span>
                          {item.to === '/approvals' && !!pending && (
                            <span className="count alert">{pending}</span>
                          )}
                          {countKey && counts && (
                            <span className="count">{counts[countKey]}</span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          <div className="avatar">{initials(actor.fullName ?? '')}</div>
          <div className="who">
            <div className="n">{actor.fullName}</div>
            <div className="r">{admin ? 'Administrator' : 'Site staff'}</div>
          </div>
          <button className="icon-btn" onClick={() => void signOut()} title="Sign out" aria-label="Sign out">
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      {menuOpen && <div className="sidebar-scrim" onClick={() => setMenuOpen(false)} />}

      <div className="main">
        <header className="topbar">
          <button className="icon-btn menu-btn" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu">
            <Icon name="menu" />
          </button>
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <h1 className="page-title">{title}</h1>
          </div>
          <div className="topbar-actions" style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {actions}
            <button className="icon-btn bell-btn" onClick={() => navigate('/notifications')}
                    title={unread ? `${unread} unread` : 'Notifications'}
                    aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}>
              <Icon name="bell" />
              {unread > 0 && <span className="bell-dot">{unread > 9 ? '9+' : unread}</span>}
            </button>
          </div>
        </header>

        <main className="content">
          <div className="view">{children}</div>
        </main>
      </div>
    </div>
  );
}

